import { useState } from "react";
import axios from "axios";
import { useRef } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export default function KataSlipByEntryType() {
  const [search, setSearch] = useState("");

  const token = sessionStorage.getItem("token");

  const tablePrintRef = useRef();
  const modalPrintRef = useRef();
  const [entryType, setEntryType] = useState("INBOUND");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({
    warehouse: "",
    company: "",
    stack: "",
    commodity: "",
    variety: "",
    vessel: "", // ✅ NEW
    fromDate: "", // ✅ NEW
    toDate: "",
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredData = data.filter((item) => {
    const keyword = search.toLowerCase();

    // OUTBOUND DTR uses CDD document date.
    // INBOUND continues using actual inbound date.
    const itemDate =
      item.entry_type === "INBOUND"
        ? item.dateIn
        : item.cddDocumentDate || item.dateOut;

    const itemDateObj = itemDate ? new Date(itemDate) : null;
    const fromDateObj = filters.fromDate ? new Date(filters.fromDate) : null;
    const toDateObj = filters.toDate ? new Date(filters.toDate) : null;

    const matchesFilters =
      (!filters.warehouse || item.warehouse === filters.warehouse) &&
      (!filters.company || item.companyName === filters.company) &&
      (!filters.stack || item.stack === filters.stack) &&
      (!filters.commodity || item.product === filters.commodity) &&
      (!filters.variety || item.variety === filters.variety) &&
      (!filters.vessel || item.vesselName === filters.vessel) &&
      (!fromDateObj || (itemDateObj && itemDateObj >= fromDateObj)) &&
      (!toDateObj || (itemDateObj && itemDateObj <= toDateObj));

    // ✅ GLOBAL SEARCH
    const matchesSearch =
      !search ||
      item.companyName?.toLowerCase().includes(keyword) ||
      item.vehicleNo?.toLowerCase().includes(keyword) ||
      item.product?.toLowerCase().includes(keyword) ||
      item.variety?.toLowerCase().includes(keyword) ||
      item.crdNo?.toLowerCase().includes(keyword) ||
      item.cddNo?.toLowerCase().includes(keyword) ||
      item.warehouse?.toLowerCase().includes(keyword) ||
      item.stack?.toLowerCase().includes(keyword);

    return matchesFilters && matchesSearch;
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        "https://irc.zentelex.com/api/reports/kataslip-weighment/by-crd-cdd",
        {
          params: {
            entry_type: entryType,
            fromDate: filters.fromDate || undefined,
            toDate: filters.toDate || undefined,
          },

          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setData(res.data.data || []);
    } catch (error) {
      alert(error.response?.data?.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const totalNetWt = filteredData.reduce(
    (sum, item) => sum + Number(item.netWt || 0),
    0,
  );

  const uniqueValues = (key) => [
    ...new Set(data.map((item) => item[key]).filter(Boolean)),
  ];

  const handlePrint = (type = "table") => {
    const element =
      type === "modal" ? modalPrintRef.current : tablePrintRef.current;

    if (!element) {
      console.error("Print element not found");
      return;
    }

    const printContents = element.innerHTML;

    const printWindow = window.open("", "", "width=900,height=700");

    printWindow.document.write(`
    <html>
      <head>
        <title>DTR Report</title>
        <style>
          body { font-family: Arial; padding: 20px; }
          table { width:100%; border-collapse: collapse; }
          table, th, td { border:1px solid #ccc; }
          th, td { padding:8px; text-align:left; }
        </style>
      </head>
      <body>
        ${printContents}
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.print();
  };

  const handleExportTableExcel = () => {
    if (!filteredData.length) {
      alert("No data to export");
      return;
    }

    const exportData = filteredData.map((item, index) => {
      const avgWt = item.noOfBags
        ? (
            ((entryType === "OUTBOUND"
              ? Number(item.secondWt || 0) - Number(item.firstWt || 0)
              : Number(item.firstWt || 0) - Number(item.secondWt || 0)) *
              1000) /
            Number(item.noOfBags)
          ).toFixed(4)
        : "0.0000";

      return {
        "Sl No": index + 1,
        Date:
          entryType === "INBOUND"
            ? item.dateIn
            : item.cddDocumentDate || item.dateOut,
        "Client Name": item.companyName,
        Warehouse: item.warehouse,
        ...(entryType === "INBOUND" && { "CRD No": item.crdNo }),
        ...(entryType === "OUTBOUND" && { "CDD No": item.cddNo }),
        "Truck No": item.vehicleNo,
        Commodity: item.product,
        Variety: item.variety,
        "No. of Bags": item.noOfBags,
        "First Weight": item.firstWt,
        "Second Weight": item.secondWt,
        Quantity: item.netWt,
        "Avg Weight": avgWt,
        ...(entryType === "OUTBOUND" && {
          "Moisture (%)": item.moisture || "-",
        }),
        Remarks: item.miscellaneous || "",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DTR Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(
      file,
      `DTR_${entryType}_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const handleExportModalExcel = () => {
    if (!selectedItem) {
      alert("No data to export");
      return;
    }

    const exportData = {
      ...(selectedItem.entry_type === "INBOUND" && {
        "CRD No": selectedItem.crdNo,
      }),

      ...(selectedItem.entry_type === "OUTBOUND" && {
        "CDD No": selectedItem.cddNo,
      }),

      "Vehicle No": selectedItem.vehicleNo,
      Warehouse: selectedItem.warehouse,
      "Vehicle Type": selectedItem.vehicleType,
      Product: selectedItem.product,
      Variety: selectedItem.variety,
      Company: selectedItem.companyName,
      "No. of Bags": selectedItem.noOfBags,
      "First Wt": selectedItem.firstWt,
      "Second Wt": selectedItem.secondWt,
      "Net Wt": selectedItem.netWt,
      "Date In": selectedItem.dateIn,
      "Date Out": selectedItem.dateOut,
      "Container No": selectedItem.containerNo,
      Amount: selectedItem.amount,
      "Labour Contractor": selectedItem.labourContractorName,
      Surveyor: selectedItem.surveyorName,
      Miscellaneous: selectedItem.miscellaneous,
      Stack: selectedItem.stack,
    };

    const worksheet = XLSX.utils.json_to_sheet([exportData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "DTR Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const file = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(
      file,
      `DTR_${selectedItem.entry_type}_${selectedItem.vehicleNo}.xlsx`,
    );
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="card shadow-lg border-0 mb-4 bg-primary text-white">
        <div className="card-body">
          <h3 className="fw-bold mb-1">Daily Transaction Report</h3>
          <p className="mb-0 opacity-75">
            Inbound / Outbound Kata Slip Weighment Summary
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="card shadow-sm border-0 mb-4 bg-light">
        <div className="card-body row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label fw-semibold text-primary">
              Entry Type
            </label>
            <select
              className="form-select border-primary"
              value={entryType}
              onChange={(e) => {
                setEntryType(e.target.value);
                setData([]);
              }}
            >
              <option value="INBOUND">INBOUND</option>
              <option value="OUTBOUND">OUTBOUND</option>
            </select>
          </div>

          <div className="col-md-3">
            <button
              className="btn btn-primary w-100 fw-semibold shadow-sm"
              onClick={fetchData}
              disabled={loading}
            >
              {loading ? "Loading..." : "Fetch Report"}
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-header bg-primary text-white fw-bold">
          <i className="bi bi-funnel-fill me-2"></i> Advanced Filters
        </div>

        <div className="card-body">
          <div className="row g-3 align-items-end">
            {/* Warehouse */}
            <div className="col-md-3">
              <label className="form-label fw-semibold text-muted">
                Warehouse
              </label>
              <select
                className="form-select shadow-sm"
                name="warehouse"
                value={filters.warehouse}
                onChange={handleFilterChange}
              >
                <option value="">All Warehouses</option>
                {uniqueValues("warehouse").map((w) => (
                  <option key={w} value={w}>
                    {w}
                  </option>
                ))}
              </select>
            </div>

            {/* Company */}
            <div className="col-md-3">
              <label className="form-label fw-semibold text-muted">
                Company
              </label>
              <select
                className="form-select shadow-sm"
                name="company"
                value={filters.company}
                onChange={handleFilterChange}
              >
                <option value="">All Companies</option>
                {uniqueValues("companyName").map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Stack */}
            <div className="col-md-2">
              <label className="form-label fw-semibold text-muted">Stack</label>
              <select
                className="form-select shadow-sm"
                name="stack"
                value={filters.stack}
                onChange={handleFilterChange}
              >
                <option value="">All Stacks</option>
                {uniqueValues("stack").map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            {/* Commodity */}
            <div className="col-md-2">
              <label className="form-label fw-semibold text-muted">
                Commodity
              </label>
              <select
                className="form-select shadow-sm"
                name="commodity"
                value={filters.commodity}
                onChange={handleFilterChange}
              >
                <option value="">All Commodities</option>
                {uniqueValues("product").map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>

            {/* Variety */}
            <div className="col-md-2">
              <label className="form-label fw-semibold text-muted">
                Variety
              </label>
              <select
                className="form-select shadow-sm"
                name="variety"
                value={filters.variety}
                onChange={handleFilterChange}
              >
                <option value="">All Varieties</option>
                {uniqueValues("variety").map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>

            {/* Buttons */}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-2">
          <label className="form-label fw-semibold text-muted">From Date</label>
          <input
            type="date"
            className="form-control shadow-sm"
            name="fromDate"
            value={filters.fromDate}
            onChange={handleFilterChange}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label fw-semibold text-muted">To Date</label>
          <input
            type="date"
            className="form-control shadow-sm"
            name="toDate"
            value={filters.toDate}
            onChange={handleFilterChange}
          />
        </div>
        <div className="col-md-3 d-flex gap-2">
          <button
            className="btn btn-outline-secondary w-100 shadow-sm"
            onClick={() => {
              setFilters({
                warehouse: "",
                company: "",
                stack: "",
                commodity: "",
                variety: "",
                vessel: "",
                fromDate: "",
                toDate: "",
              });
              setSearch("");
            }}
          >
            <i className="bi bi-arrow-counterclockwise me-1"></i>
            Reset
          </button>
        </div>
      </div>

      <br />

      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control shadow-sm"
                placeholder="🔍 Search Client / Truck / CRD / Commodity..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}

      <div ref={tablePrintRef}>
        <div
          style={{
            textAlign: "center",
            padding: "15px 10px",
            fontFamily: "Arial, Helvetica, sans-serif",
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "bold",
              letterSpacing: "1px",
            }}
          >
            IRC LIMITED
          </div>

          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              marginTop: "5px",
            }}
          >
            IRC HOUSE, 1 SUNYAT SEN STREET , KOLKATA-700012
          </div>

          <div
            style={{
              fontSize: "18px",
              marginTop: "8px",
            }}
          >
            TELEPHONE NO. : +91-33-2236-5110 (5 Lines) FAX: +91-33-2225 5936,
            Email : ho@ircl.in
          </div>

          <div
            style={{
              fontSize: "18px",
              marginTop: "5px",
            }}
          >
            PAN NO : AACCP2218A &nbsp;&nbsp;&nbsp; TAN: CALP02108B
            &nbsp;&nbsp;&nbsp; GSTIN: 19AACCP2218A1ZY
          </div>
        </div>
        <br />
        <div>
          <h1>DTR REPORT</h1>

          {entryType && (
            <span
              className={`badge fs-6 ${
                entryType === "INBOUND" ? "bg-success" : "bg-warning text-dark"
              }`}
            >
              {entryType}
            </span>
          )}
        </div>
        {data.length > 0 && (
          <div className="card shadow-sm border-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-primary text-center">
                  <tr>
                    <th>Sl no.</th>
                    <th>Date</th> {/* ✅ ADD */}
                    <th>Client name</th>
                    <th>Warehouse name</th>
                    {entryType !== "OUTBOUND" && <th>CRD No</th>}
                    {entryType !== "INBOUND" && <th>CDD No</th>}
                    <th>Truck No</th>
                    <th>Commodity</th>
                    <th>Variety</th>
                    <th>No. of Bags</th>
                    <th>First Weight</th>
                    <th>Second Weight</th>
                    <th>Quantity</th>
                    <th className="text-end">Avg Wt</th>
                    {entryType === "OUTBOUND" && <th>Moisture (%)</th>}
                    <th>Remarks</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredData.map((item, index) => (
                    <tr key={item.id}>
                      <td className="text-center">{index + 1}</td>

                      <td className="text-center fw-semibold">
                        {entryType === "INBOUND" && item.dateIn
                          ? new Date(item.dateIn).toLocaleDateString("en-GB")
                          : entryType === "OUTBOUND" &&
                              (item.cddDocumentDate || item.dateOut)
                            ? new Date(
                                item.cddDocumentDate || item.dateOut
                              ).toLocaleDateString("en-GB")
                            : "-"}
                      </td>
                      <td>{item.companyName}</td>
                      <td>{item.warehouse}</td>
                      {entryType !== "OUTBOUND" && <td>{item.crdNo}</td>}
                      {entryType !== "INBOUND" && <td>{item.cddNo}</td>}

                      <td>{item.vehicleNo}</td>
                      <td>{item.product}</td>
                      <td>{item.variety}</td>

                      <td>{item.noOfBags}</td>
                      <td>{item.firstWt}</td>
                      <td>{item.secondWt}</td>
                      <td>{item.netWt}</td>

                      <td className="text-end">
                        {item.noOfBags
                          ? (
                              ((entryType === "OUTBOUND"
                                ? Number(item.secondWt || 0) -
                                  Number(item.firstWt || 0)
                                : Number(item.firstWt || 0) -
                                  Number(item.secondWt || 0)) *
                                1000) /
                              Number(item.noOfBags)
                            ).toFixed(4)
                          : "0.0000"}
                      </td>
                      {entryType === "OUTBOUND" && (
                        <td className="text-center">
                          {item.moisture ? `${item.moisture}%` : "-"}
                        </td>
                      )}

                      <td>{item.miscellaneous}</td>

                      <td className="text-center">
                        <button
                          className="btn btn-sm btn-outline-primary fw-semibold"
                          onClick={() => setSelectedItem(item)}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Total */}
            <div className="card-footer bg-dark text-white text-end fw-bold">
              <button
                className="btn btn-secondary"
                onClick={() => handlePrint("table")}
              >
                🖨 Print
              </button>
              <button
                className="btn btn-success ms-2"
                onClick={handleExportTableExcel}
              >
                📥 Export Table
              </button>
              Total Net Weight :{" "}
              <span className="text-warning">{totalNetWt.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>

      {!loading && data.length === 0 && (
        <div className="alert alert-secondary text-center shadow-sm">
          No records found
        </div>
      )}

      {/* Modal */}
      {selectedItem && (
        <>
          <div
            className="modal-backdrop show"
            onClick={() => setSelectedItem(null)}
          ></div>

          <div
            className="modal show"
            style={{ display: "block", zIndex: 1055 }}
          >
            <div
              className="modal-dialog modal-dialog-centered modal-lg"
              ref={modalPrintRef}
            >
              <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                    DTR Report
                    {selectedItem?.entry_type && (
                      <span
                        className={`badge ${
                          selectedItem.entry_type === "INBOUND"
                            ? "bg-success"
                            : "bg-warning text-dark"
                        }`}
                      >
                        {selectedItem.entry_type}
                      </span>
                    )}
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setSelectedItem(null)}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row g-3">
                    {Object.entries({
                      ...(selectedItem.entry_type === "INBOUND" && {
                        "CRD No": selectedItem.crdNo,
                      }),

                      ...(selectedItem.entry_type === "OUTBOUND" && {
                        "CDD No": selectedItem.cddNo,
                      }),

                      "Vehicle No": selectedItem.vehicleNo,
                      Warehouse: selectedItem.warehouse, // ✅ ADD HERE

                      "Vehicle Type": selectedItem.vehicleType,
                      Product: selectedItem.product,
                      Variety: selectedItem.variety,
                      Company: selectedItem.companyName,
                      "No. of Bags": selectedItem.noOfBags,
                      "First Wt": selectedItem.firstWt,
                      "Second Wt": selectedItem.secondWt,
                      "Net Wt": selectedItem.netWt,
                      "Date In": selectedItem.dateIn,
                      "Date Out": selectedItem.dateOut,
                      "Container No": selectedItem.containerNo,

                      Amount: `₹ ${selectedItem.amount}`,
                      "Labour Contractor": selectedItem.labourContractorName,
                      Surveyor: selectedItem.surveyorName,
                      Miscellaneous: selectedItem.miscellaneous,
                      Stack: selectedItem.stack,
                    }).map(
                      ([label, value]) =>
                        value && (
                          <div className="col-md-6" key={label}>
                            <small className="text-muted">{label}</small>
                            <div className="fw-semibold text-dark">{value}</div>
                          </div>
                        ),
                    )}
                  </div>
                </div>

                <div className="modal-footer d-flex justify-content-between">
                  <button
                    className="btn btn-secondary"
                    onClick={() => handlePrint("modal")}
                  >
                    🖨 Print
                  </button>

                  <button
                    className="btn btn-success"
                    onClick={handleExportModalExcel}
                  >
                    📥 Export to Excel
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setSelectedItem(null)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
